import { swapItemGift } from '@theme/item-gifts';

const SCOPE = '[data-item-gift-choice-scope]';
const ROW = '[data-item-gift][data-key]';
const states = new Map();
const busyOffers = new Set();
const rendered = new WeakMap();
let scheduled = false;

// Only selectable variants count. A single configured gift must not gain an
// empty "Change" control merely because another choice is sold out.
export function availableGiftChoices(config) {
  if (config?.active !== true || !Array.isArray(config.rewards)) return [];
  const seen = new Set();
  return config.rewards.flatMap((reward) => {
    if (reward?.available !== true || !Array.isArray(reward.variants)) return [];
    const variants = reward.variants.filter((variant) => {
      const id = Number(variant?.id);
      if (variant?.available !== true || !Number.isSafeInteger(id) || id <= 0 || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    return variants.length ? [{ ...reward, variants, needsVariantChoice: reward.variants.length > 1 }] : [];
  });
}

export function canChangeGift(config) {
  return availableGiftChoices(config).reduce((total, reward) => total + reward.variants.length, 0) > 1;
}

export function giftVariantLabel(title) {
  return String(title || '').replace(/\bo\/s\b/gi, 'OS').split(/\s*\/\s*/)
    .filter((part) => !/^(?:default title|os|o\/s|one size|one size fits all)$/i.test(part.trim()))
    .join(' / ');
}

function stateKey(row) {
  return `${row.dataset.itemGift}|${row.dataset.key}`;
}

function stateFor(row) {
  const key = stateKey(row);
  if (!states.has(key)) states.set(key, { open: false, error: '', selections: new Map(), pendingVariant: 0 });
  return states.get(key);
}

function configsFor(scope) {
  try {
    const configs = JSON.parse(scope.querySelector('[data-item-gift-choice-configs]')?.textContent || '[]');
    return Array.isArray(configs) ? configs : [];
  } catch (_) { return []; }
}

function contextFor(row) {
  const scope = row?.closest(SCOPE);
  const config = scope && configsFor(scope).find((offer) => offer.offer === row.dataset.itemGift);
  if (!config || !canChangeGift(config)) return null;
  return { scope, row, config, state: stateFor(row) };
}

function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function choiceCard(reward, row, state) {
  const currentVariant = Number(row.dataset.variantId);
  const ownsCurrent = reward.variants.some((variant) => Number(variant.id) === currentVariant);
  const card = element('div', 'item-gift-choice-card');
  card.setAttribute('role', 'listitem');
  card.dataset.selected = String(ownsCurrent);
  card.dataset.giftProductId = String(reward.productId);
  if (reward.image) {
    const image = element('img', 'item-gift-choice-card__image');
    image.src = reward.image;
    image.alt = reward.title || 'Gift';
    image.loading = 'lazy';
    image.width = 320;
    image.height = 320;
    card.append(image);
  }
  card.append(element('p', 'item-gift-choice-card__title', reward.title));
  const subtitle = reward.needsVariantChoice ? '' : giftVariantLabel(reward.variants[0].title);
  if (subtitle) card.append(element('p', 'item-gift-choice-card__variant', subtitle));

  let selectedVariant = Number(state.selections.get(String(reward.productId)) || 0);
  if (!reward.variants.some((variant) => Number(variant.id) === selectedVariant)) {
    selectedVariant = ownsCurrent ? currentVariant : reward.needsVariantChoice ? 0 : Number(reward.variants[0].id);
  }
  if (reward.needsVariantChoice) {
    const select = element('select', 'item-gift-choice-card__select');
    select.dataset.itemGiftChoiceVariant = '';
    select.setAttribute('aria-label', `Choose an option for ${reward.title}`);
    const placeholder = element('option', '', 'Choose an option');
    placeholder.value = '';
    select.append(placeholder);
    reward.variants.forEach((variant) => {
      const option = element('option', '', variant.title);
      option.value = String(variant.id);
      select.append(option);
    });
    select.value = selectedVariant ? String(selectedVariant) : '';
    card.append(select);
  }
  const button = element('button', 'item-gift-choice-card__button');
  button.type = 'button';
  button.dataset.itemGiftChoiceSelect = '';
  button.dataset.variantId = String(selectedVariant || '');
  button.dataset.giftTitle = [reward.title, selectedVariant
    ? giftVariantLabel(reward.variants.find((variant) => Number(variant.id) === selectedVariant)?.title) : '']
    .filter(Boolean).join(', ');
  card.append(button);
  updateCard(card, row, state);
  return card;
}

function updateCard(card, row, state) {
  const button = card.querySelector('[data-item-gift-choice-select]');
  const select = card.querySelector('[data-item-gift-choice-variant]');
  const variantId = Number(select ? select.value : button.dataset.variantId);
  const current = variantId === Number(row.dataset.variantId);
  const busy = busyOffers.has(row.dataset.itemGift);
  button.dataset.variantId = String(variantId || '');
  button.dataset.current = String(current);
  button.disabled = busy || !variantId || current;
  if (select) select.disabled = busy;
  button.textContent = busy && state.pendingVariant === variantId ? 'Updating…'
    : current ? (Number(row.dataset.quantity) > 1 ? `${row.dataset.quantity} in cart` : 'Selected')
      : variantId ? 'Select' : 'Choose an option';
  button.setAttribute('aria-label', current ? `${button.dataset.giftTitle}, in your cart`
    : `Select ${button.dataset.giftTitle}`);
}

function renderRow(row, config) {
  const wrap = row.querySelector('[data-item-gift-choice-trigger-wrap]');
  const toggle = row.querySelector('[data-item-gift-choice-toggle]');
  const cell = row.querySelector('[data-item-gift-choice-cell]');
  if (!wrap || !toggle || !cell) return;
  const enabled = canChangeGift(config);
  wrap.hidden = !enabled;
  if (!enabled) {
    cell.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
    rendered.delete(row);
    return;
  }
  const state = stateFor(row);
  const label = String(config.changeLabel || 'Change gift').trim() || 'Change gift';
  const panel = cell.querySelector('[data-item-gift-choice-panel]');
  const grid = cell.querySelector('[data-item-gift-choice-grid]');
  const note = cell.querySelector('[data-item-gift-choice-note]');
  const status = cell.querySelector('[data-item-gift-choice-status]');
  const busy = busyOffers.has(row.dataset.itemGift);
  if (toggle.textContent !== label) toggle.textContent = label;
  toggle.setAttribute('aria-expanded', String(state.open));
  toggle.setAttribute('aria-label', `${label} for ${row.querySelector('.cart-items__title')?.textContent.trim() || 'your gift'}`);
  panel.setAttribute('aria-label', label);
  panel.setAttribute('aria-busy', String(busy));
  cell.hidden = !state.open;
  note.hidden = Number(row.dataset.quantity) < 2;
  status.hidden = !busy && !state.error;
  status.dataset.error = String(Boolean(state.error));
  status.textContent = busy ? 'Updating your gift…' : state.error;
  if (!state.open) return;
  const signature = JSON.stringify([config.rewards, row.dataset.variantId, row.dataset.quantity]);
  const previous = rendered.get(row);
  if (previous?.signature !== signature || previous.grid !== grid || !grid.firstElementChild) {
    const choices = availableGiftChoices(config);
    const currentVariant = Number(row.dataset.variantId);
    choices.sort((a, b) => Number(b.variants.some((variant) => Number(variant.id) === currentVariant))
      - Number(a.variants.some((variant) => Number(variant.id) === currentVariant)));
    grid.replaceChildren(...choices.map((reward) => choiceCard(reward, row, state)));
    rendered.set(row, { signature, grid });
  }
  grid.querySelectorAll('.item-gift-choice-card').forEach((card) => updateCard(card, row, state));
}

function scan() {
  scheduled = false;
  document.querySelectorAll(SCOPE).forEach((scope) => {
    const configs = configsFor(scope);
    scope.querySelectorAll(ROW).forEach((row) => {
      renderRow(row, configs.find((config) => config.offer === row.dataset.itemGift));
    });
  });
}

function scheduleScan() {
  if (scheduled) return;
  scheduled = true;
  queueMicrotask(scan);
}

function closeRow(row, focus = true) {
  const context = contextFor(row);
  if (!context) return;
  context.state.open = false;
  renderRow(row, context.config);
  if (focus) row.querySelector('[data-item-gift-choice-toggle]')?.focus({ preventScroll: true });
}

function matchingRow(scope, offer, variantId, key) {
  const currentScope = scope?.isConnected ? scope : [...document.querySelectorAll(SCOPE)]
    .find((candidate) => candidate.dataset.itemGiftChoiceScope === scope?.dataset.itemGiftChoiceScope);
  const rows = [...(currentScope?.querySelectorAll(ROW) || [])];
  return rows.find((row) => row.dataset.itemGift === offer && (!key || row.dataset.key === key)
    && (!variantId || Number(row.dataset.variantId) === Number(variantId)));
}

function announce(message) {
  let status = document.querySelector('[data-item-gift-choice-announcement]');
  if (!status) {
    status = element('div', 'visually-hidden');
    status.dataset.itemGiftChoiceAnnouncement = '';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    document.body.append(status);
  }
  status.textContent = message;
}

async function selectGift(button, context) {
  const { row, scope, config, state } = context;
  const offer = config.offer;
  const variantId = Number(button.dataset.variantId);
  if (busyOffers.has(offer) || button.disabled || !variantId) return;
  const originalVariant = Number(row.dataset.variantId);
  const key = stateKey(row);
  state.error = '';
  state.pendingVariant = variantId;
  busyOffers.add(offer);
  scan();
  try {
    await swapItemGift({ offer, lineKey: row.dataset.key, variantId });
    const shouldFocus = state.open;
    state.open = false;
    state.pendingVariant = 0;
    states.delete(key);
    busyOffers.delete(offer);
    scan();
    announce(`Gift updated to ${button.dataset.giftTitle}.`);
    const updated = matchingRow(scope, offer, variantId);
    if (updated && shouldFocus) updated.querySelector('[data-item-gift-choice-toggle]')?.focus({ preventScroll: true });
  } catch (error) {
    busyOffers.delete(offer);
    state.pendingVariant = 0;
    state.error = error?.message || 'Your gift could not be changed. Please try again.';
    state.open = true;
    // A restored gift can have a new line key. Carry its error/open state over
    // so the cart refresh cannot silently erase the result of the attempt.
    const restored = matchingRow(scope, offer, originalVariant, row.dataset.key)
      || matchingRow(scope, offer, originalVariant);
    if (restored) {
      states.delete(key);
      states.set(stateKey(restored), state);
    }
    scan();
    announce(state.error);
  }
}

export function initItemGiftChoices() {
  if (window.__mmItemGiftChoices) return;
  window.__mmItemGiftChoices = true;
  document.addEventListener('click', (event) => {
    const control = event.target.closest?.('[data-item-gift-choice-toggle], [data-item-gift-choice-close], [data-item-gift-choice-select]');
    if (!control) return;
    const row = control.closest(ROW);
    const context = contextFor(row);
    if (!context) return;
    event.preventDefault();
    if (control.hasAttribute('data-item-gift-choice-close')) return closeRow(row);
    if (control.hasAttribute('data-item-gift-choice-select')) return void selectGift(control, context);
    context.state.open = !context.state.open;
    renderRow(row, context.config);
    if (context.state.open) row.querySelector('[data-item-gift-choice-panel]')?.focus({ preventScroll: true });
  });
  document.addEventListener('change', (event) => {
    const select = event.target.closest?.('[data-item-gift-choice-variant]');
    if (!select) return;
    const row = select.closest(ROW);
    const context = contextFor(row);
    if (!context) return;
    const card = select.closest('.item-gift-choice-card');
    context.state.selections.set(card.dataset.giftProductId, select.value);
    const button = card.querySelector('[data-item-gift-choice-select]');
    const reward = context.config.rewards.find((entry) => String(entry.productId) === card.dataset.giftProductId);
    button.dataset.giftTitle = [reward?.title, giftVariantLabel(reward?.variants.find((variant) => String(variant.id) === select.value)?.title)]
      .filter(Boolean).join(', ');
    updateCard(card, row, context.state);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const panel = event.target.closest?.('[data-item-gift-choice-panel]');
    if (!panel) return;
    event.preventDefault();
    event.stopPropagation();
    closeRow(panel.closest(ROW));
  });
  document.addEventListener('cart:update', scheduleScan);
  document.addEventListener('shopify:section:load', scheduleScan);
  const observer = new MutationObserver((records) => {
    if (records.some((record) => {
      const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
      if (target?.closest?.('[data-item-gift-choice-panel], [data-item-gift-choice-trigger-wrap]')) return false;
      return target?.closest?.(SCOPE) || [...record.addedNodes].some((node) => node.nodeType === 1
        && (node.matches?.(SCOPE) || node.querySelector?.(SCOPE)));
    })) scheduleScan();
  });
  observer.observe(document.body, { childList: true, subtree: true, attributes: true,
    attributeFilter: ['data-key', 'data-quantity', 'data-variant-id', 'data-item-gift'] });
  scan();
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initItemGiftChoices, { once: true });
  else initItemGiftChoices();
}
